import { cn } from "@/utils/utils";
import LandingIcon from "./LandingIcon";

interface CatalogPaginationProps {
  page: number;
  totalPages: number;
  onPageChange: (page: number) => void;
}

function getVisiblePages(page: number, totalPages: number): number[] {
  if (totalPages <= 5) {
    return Array.from({ length: totalPages }, (_, i) => i + 1);
  }

  let start = Math.max(1, page - 2);
  const end = Math.min(totalPages, start + 4);
  start = Math.max(1, end - 4);

  return Array.from({ length: end - start + 1 }, (_, i) => start + i);
}

const navBtnClass =
  "flex items-center gap-1 px-2 py-2 text-[14px] font-medium text-landing-body hover:text-landing-text disabled:cursor-default disabled:opacity-40 md:px-3";

export default function CatalogPagination({
  page,
  totalPages,
  onPageChange,
}: CatalogPaginationProps) {
  if (totalPages <= 1) {
    return null;
  }

  const pages = getVisiblePages(page, totalPages);

  return (
    <nav
      className="flex items-center justify-center gap-4 py-8 md:gap-8"
      aria-label="Pagination"
    >
      <button
        type="button"
        disabled={page <= 1}
        onClick={() => onPageChange(page - 1)}
        className={navBtnClass}
      >
        <LandingIcon name="chevronLeft" className="h-4 w-4" />
        Prev
      </button>

      <div className="flex items-center gap-3 md:gap-4">
        {pages.map((p) => {
          const isActive = p === page;
          return (
            <button
              key={p}
              type="button"
              onClick={() => onPageChange(p)}
              aria-current={isActive ? "page" : undefined}
              className={cn(
                "relative inline-flex min-w-[36px] flex-col items-center px-2 py-2 text-[14px]",
                isActive
                  ? "font-bold text-landing-text"
                  : "font-medium text-[var(--landing-pagination-inactive)] hover:text-landing-body"
              )}
            >
              {String(p).padStart(2, "0")}
              {isActive ? (
                <span
                  className="absolute bottom-0 left-1/2 h-[3px] w-[calc(100%+4px)] -translate-x-1/2 rounded-full bg-[var(--landing-pagination-underline)]"
                  aria-hidden
                />
              ) : null}
            </button>
          );
        })}
      </div>

      <button
        type="button"
        disabled={page >= totalPages}
        onClick={() => onPageChange(page + 1)}
        className={navBtnClass}
      >
        Next
        <LandingIcon name="chevronRight" className="h-4 w-4" />
      </button>
    </nav>
  );
}
